import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/crm/CrmShell";
import { Btn, Field, inputCls, EmptyState, Modal } from "@/components/crm/kit";
import { Layers, Plus, Pencil } from "lucide-react";

export default function ServiceCatalog() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [add, setAdd] = useState({ name: "", milestone_label: "" });
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/crm/service-catalog");
    setRows(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!add.name.trim()) return toast.error("Service name is required");
    try { await api.post("/crm/service-catalog", add); toast.success("Service added"); setShowAdd(false); setAdd({ name: "", milestone_label: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try { await api.patch(`/crm/service-catalog/${edit.id}`, { name: edit.name, milestone_label: edit.milestone_label }); toast.success("Saved"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (s) => {
    try { await api.patch(`/crm/service-catalog/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Service Catalog" subtitle="Services clients can participate in (admin only)">
        <Btn onClick={() => setShowAdd(true)} data-testid="sc-new"><Plus className="h-4 w-4" /> Add Service</Btn>
      </PageHeader>

      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState icon={Layers} title="No services" hint="Add the services your clients can participate in." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((s) => (
              <div key={s.id} data-testid={`sc-row-${s.id}`} className={`rounded-xl border p-4 ${s.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-display font-bold text-slate-800">{s.name}</p>
                    {s.milestone_label && <p className="mt-0.5 text-xs text-slate-500">Milestone: <span className="font-semibold text-primary">{s.milestone_label}</span></p>}
                    {s.is_default && <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-400">Default</span>}
                  </div>
                  <button onClick={() => setEdit({ ...s, milestone_label: s.milestone_label || "" })} data-testid={`sc-edit-${s.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                </div>
                <button onClick={() => toggle(s)} data-testid={`sc-toggle-${s.id}`} className={`mt-3 rounded-full px-2.5 py-0.5 text-xs font-bold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                  {s.active ? "Active" : "Inactive"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Service">
        <Field label="Service name *"><input data-testid="sc-name" className={inputCls} value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} placeholder="e.g. Chronic Care Management" /></Field>
        <Field label="Service milestone (optional)"><input data-testid="sc-milestone" className={inputCls} value={add.milestone_label} onChange={(e) => setAdd({ ...add, milestone_label: e.target.value })} placeholder="e.g. First enrollment completed" /></Field>
        <Btn onClick={create} data-testid="sc-save" className="mt-2 w-full justify-center">Add Service</Btn>
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit Service">
        {edit && (
          <>
            <Field label="Service name"><input data-testid="sc-edit-name" className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Service milestone"><input className={inputCls} value={edit.milestone_label} onChange={(e) => setEdit({ ...edit, milestone_label: e.target.value })} /></Field>
            <Btn onClick={saveEdit} data-testid="sc-edit-save" className="mt-2 w-full justify-center">Save</Btn>
          </>
        )}
      </Modal>
    </div>
  );
}
